import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactPathways = () => {
  const [activePathway, setActivePathway] = useState('project');

  const pathways = [
    {
      id: 'project',
      title: 'Project Inquiry',
      description: 'Start a conversation about your next transformation project',
      icon: 'Briefcase',
      color: 'bg-primary',
      features: [
        'Intelligent needs assessment',
        'Budget and timeline planning',
        'Direct routing to practice leaders',
        'Immediate project scoping'
      ]
    },
    {
      id: 'partnership',
      title: 'Partnership Discussion',
      description: 'Explore collaboration opportunities with our team',
      icon: 'Handshake',
      color: 'bg-secondary',
      features: [
        'Strategic alliance opportunities',
        'Technology integration partnerships',
        'Referral program enrollment',
        'Joint venture discussions'
      ]
    },
    {
      id: 'media',
      title: 'Media & Press',
      description: 'Connect with our communications team',
      icon: 'Newspaper',
      color: 'bg-accent',
      features: [
        'Press release distribution',
        'Executive interview requests',
        'Industry commentary',
        'Speaking engagement inquiries'
      ]
    },
    {
      id: 'general',
      title: 'General Inquiry',
      description: 'Any other questions or information requests',
      icon: 'MessageSquare',
      color: 'bg-brand-charcoal',
      features: [
        'Company information',
        'Career opportunities',
        'Vendor applications',
        'General support'
      ]
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/20 to-secondary/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-heading">
            Choose Your Connection Path
          </h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            We've designed multiple pathways to ensure you connect with the right team member for your specific needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {pathways.map((pathway) => (
            <div
              key={pathway.id}
              className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                activePathway === pathway.id
                  ? 'glass-morph bg-brand-cyan/30 border-brand-cyan/50 shadow-md'
                  : 'glass-morph bg-white/70 backdrop-blur-sm border-gray-200 hover:border-brand-cyan/30'
              }`}
              onClick={() => setActivePathway(pathway.id)}
            >
              <div className={`w-12 h-12 ${pathway.color} rounded-lg flex items-center justify-center mb-4`}>
                <Icon
                  name={pathway.icon}
                  size={24}
                  className={pathway.id === 'partnership' ? 'text-primary' : 'text-white'} // Apply primary color to partnership icon
                />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {pathway.title}
              </h3>

              <p className="text-sm text-gray-700 mb-4">
                {pathway.description}
              </p>

              <Button
                variant={activePathway === pathway.id ? 'default' : 'outline'}
                size="sm"
                fullWidth
                className="text-sm"
              >
                Select Path
              </Button>
            </div>
          ))}
        </div>

        {/* Active Pathway Details */}
        <div className="glass-morph bg-white/90 backdrop-blur-md border border-gray-300 rounded-xl p-8 shadow-sm">
          <div className="flex items-center mb-6">
            <div className={`w-16 h-16 ${pathways.find(p => p.id === activePathway)?.color} rounded-lg flex items-center justify-center mr-4`}>
              <Icon
                name={pathways.find(p => p.id === activePathway)?.icon}
                size={28}
                className={activePathway === 'partnership' ? 'text-primary' : 'text-white'} // Apply primary color to partnership icon in details
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {pathways.find(p => p.id === activePathway)?.title}
              </h3>
              <p className="text-gray-700">
                {pathways.find(p => p.id === activePathway)?.description}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4">What to Expect:</h4>
              <ul className="space-y-3">
                {pathways.find(p => p.id === activePathway)?.features.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <Icon name="Check" size={16} className="text-brand-green mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="flex flex-col justify-center">
              <Button
                variant="default"
                size="lg"
                className="mb-4"
              >
                <Icon name="ArrowRight" size={20} className="mr-2" />
                Start {pathways.find(p => p.id === activePathway)?.title}
              </Button>
              
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">
                  Typical response time: <span className="font-semibold text-gray-800">2-4 hours</span>
                </p>
                <div className="flex items-center justify-center">
                  <div className="w-2 h-2 bg-brand-green rounded-full mr-2 animate-pulse"></div>
                  <span className="text-sm text-brand-green font-medium">Team available now</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactPathways;